import { cn } from "@/lib/utils";
import { Flag, ShieldCheck, Link2, MessageSquareCheck, Users, CheckCircle2 } from "lucide-react";

export type BadgeDef = {
  slug: string;
  name: string;
  description: string;
  icon: typeof Flag;
  accent: "plum" | "ember" | "moss" | "sun" | "tide";
  earned: boolean;
  progress: number; // 0..100
  goal: string;
};

const ACCENT: Record<BadgeDef["accent"], { bg: string; text: string; bar: string }> = {
  plum: { bg: "bg-plum/12", text: "text-plum", bar: "bg-plum" },
  ember: { bg: "bg-ember/12", text: "text-ember", bar: "bg-ember" },
  moss: { bg: "bg-moss/12", text: "text-moss", bar: "bg-moss" },
  sun: { bg: "bg-sun/12", text: "text-sun", bar: "bg-sun" },
  tide: { bg: "bg-tide/12", text: "text-tide", bar: "bg-tide" },
};

const pct = (v: number, target: number) => Math.max(0, Math.min(100, Math.round((v / target) * 100)));

export function buildBadges(m: {
  issuesReported: number;
  verificationsGiven: number;
  followers: number;
  acceptedSolutions: number;
  spaces: number;
  issuesResolved: number;
}): BadgeDef[] {
  return [
    { slug: "reporter", name: "Reporter", description: "Documents what others walk past", icon: Flag, accent: "ember", earned: m.issuesReported >= 3, progress: pct(m.issuesReported, 3), goal: "3 reports" },
    { slug: "verifier", name: "Verifier", description: "Confirms what's really happening", icon: ShieldCheck, accent: "sun", earned: m.verificationsGiven >= 5, progress: pct(m.verificationsGiven, 5), goal: "5 verifications" },
    { slug: "connector", name: "Connector", description: "Brings people into the work", icon: Link2, accent: "tide", earned: m.followers >= 10, progress: pct(m.followers, 10), goal: "10 followers" },
    { slug: "problem-solver", name: "Problem Solver", description: "Answers that get accepted", icon: MessageSquareCheck, accent: "plum", earned: m.acceptedSolutions >= 1, progress: pct(m.acceptedSolutions, 1), goal: "1 accepted solution" },
    { slug: "community-builder", name: "Community Builder", description: "Active across Spaces", icon: Users, accent: "moss", earned: m.spaces >= 3, progress: pct(m.spaces, 3), goal: "3 Spaces" },
    { slug: "changemaker", name: "Changemaker", description: "Issues resolved for real", icon: CheckCircle2, accent: "plum", earned: m.issuesResolved >= 1, progress: pct(m.issuesResolved, 1), goal: "1 issue resolved" },
  ];
}

export function BadgeGrid({ badges, className }: { badges: BadgeDef[]; className?: string }) {
  return (
    <div className={cn("grid gap-2 sm:grid-cols-2", className)}>
      {badges.map((b) => {
        const a = ACCENT[b.accent];
        const Icon = b.icon;
        return (
          <div
            key={b.slug}
            className={cn(
              "panel flex items-start gap-3 px-3.5 py-3 transition-transform duration-200 hover:-translate-y-0.5",
              !b.earned && "opacity-70"
            )}
          >
            <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-xl", a.bg)}>
              <Icon className={cn("h-4.5 w-4.5", a.text)} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{b.name}</p>
              <p className="truncate text-xs text-muted-foreground">{b.earned ? b.description : `${b.goal} to unlock`}</p>
              {!b.earned && (
                <span className="mt-2 block h-1 overflow-hidden rounded-full bg-muted">
                  <span className={cn("block h-full rounded-full transition-[width] duration-700", a.bar)} style={{ width: `${b.progress}%` }} />
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
